import { createClient, defaultPlugins, definePlugin } from "villus";
import NProgress from "nprogress";
import useCookie, { eraseCookie } from "@/composables/useCookie";
import { getApiUrl } from "@/utils/api";
import { captureException } from "@sentry/vue";
import { flashStore } from "@/store";
import { logout } from "@/composables/useAuth";
import { organizationState } from "@/composables/useOrganization";

const { organization } = organizationState;

export const getCsrfToken = (): string | null => useCookie("XSRF-TOKEN");

export const resetCsrfToken = (): void => eraseCookie("XSRF-TOKEN");

const csrfPlugin = definePlugin(({ opContext }) => {
    const csrfToken = getCsrfToken();

    if (csrfToken === null) {
        return;
    }

    /* eslint-disable */
    opContext.headers["X-XSRF-TOKEN"] = csrfToken;
    opContext.credentials = "include";
});

const organizationContext = definePlugin(({ opContext }) => {
    opContext.headers["X-Landlord-Organization"] = organization.value?.organizationPrefixedId ?? '';
});

const userImpersonation = definePlugin(({opContext}) => {
    opContext.headers['X-Impersonated-User'] = '';
})


const localization = definePlugin(({opContext}) => {
    opContext.headers['X-Locale'] = localStorage.getItem('user-locale') ?? '';
})

const progressPlugin = definePlugin(({ afterQuery }) => {
    NProgress.start();

    afterQuery(() => {
        NProgress.done();
    });
});

export const kickUserOut = (): void => {
    return window.location.replace(
      "/?p=" + window.location.pathname
    );
}

/**
 * Reports unknown errors to sentry to avoid having to do that everywhere.
 */
const sentryReportPlugin = definePlugin(({ operation, afterQuery }) => {
    afterQuery(async ({ error }, { response }) => {
        const status = response?.status;

        if (status === 419 && ![
            "/login",
            "/",
            "",
            "/register",
            "/forgot-password",
            "/reset-password",
        ].includes(window.location.pathname)) {
            return kickUserOut();
        }

        try {
            if (response?.body?.errors && response?.body?.errors[0]?.extensions?.category === 401) {
                flashStore.doSetLoginNotice("Please Log In to view this page");
                await logout();
                return;
            }
        } catch (e) {}

        if (status !== undefined && [401, 419].includes(status)) {
            return;
        }

        // collect some information about the query that failed
        const operationContext = {
            query: operation.query,
            type: operation.type,
            variables: operation.variables,
        };

        if ((status || 200) >= 500) {
            captureException(error, {
                contexts: {
                    info: {
                        description: "received 500 response code from API",
                    },
                    operation: operationContext,
                },
            });
        }

        // Other kinds of error codes should be handled by the consuming component
        if (error) {
            captureException(error, {
                contexts: {
                    operation: operationContext,
                },
            });
        }
    });
});

export const client = createClient({
    url: getApiUrl(),
    use: [
        csrfPlugin,
        progressPlugin,
        sentryReportPlugin,
        organizationContext,
        userImpersonation,
        localization,
        ...defaultPlugins(),
    ],
    cachePolicy: "network-only",
});
